import { useMemo } from "react";
import { Card, CardContent } from "./ui/card";
import { BarChart3, Tag } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const CHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 5 };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid #E8E4DE" };
const LEGEND_STYLE = { fontSize: 12 };
const TICK_STYLE = { fontSize: 10 };
const BAR_RADIUS_TOP = [3, 3, 0, 0];
const BAR_RADIUS_RIGHT = [0, 3, 3, 0];
const TAG_COLORS = [
  "#4A6741", "#D4A373", "#6B8E6B", "#B88B5C", "#8BA888",
  "#C9A87C", "#5C7A54", "#A67B4F", "#7D9C76", "#D1B894"
];

export function AnalyticsSection({ analytics, title = "Prayer Insights", testIdPrefix = "analytics" }) {
  // Memoize pieData - always called (hooks must be called unconditionally)
  const pieData = useMemo(() => {
    if (!analytics) return [];
    return [
      { name: "Active", value: analytics.active ?? 0 },
      { name: "Answered", value: analytics.answered ?? 0 }
    ];
  }, [analytics]);

  // Early return after all hooks
  if (!analytics || analytics.total === 0) return null;

  return (
    <div data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-sage" />
        <h2 className="font-serif text-xl font-semibold text-stone-dark">{title}</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4">
        <Card className="border-stone">
          <CardContent className="p-3 sm:p-4 text-center" data-testid={`${testIdPrefix}-answer-rate`}>
            <p className="text-2xl sm:text-3xl font-semibold text-sage">{analytics.answer_rate}%</p>
            <p className="text-[10px] sm:text-xs text-stone-dark/50 mt-1">Answer Rate</p>
          </CardContent>
        </Card>
        <Card className="border-stone">
          <CardContent className="p-3 sm:p-4 text-center" data-testid={`${testIdPrefix}-avg-days`}>
            <p className="text-2xl sm:text-3xl font-semibold text-gold">{analytics.avg_days_to_answer}</p>
            <p className="text-[10px] sm:text-xs text-stone-dark/50 mt-1">Avg Days to Answer</p>
          </CardContent>
        </Card>
        <Card className="border-stone">
          <CardContent className="p-3 sm:p-4 text-center" data-testid={`${testIdPrefix}-active`}>
            <p className="text-2xl sm:text-3xl font-semibold text-stone-dark">{analytics.active}</p>
            <p className="text-[10px] sm:text-xs text-stone-dark/50 mt-1">Active Prayers</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Card className="border-stone">
          <CardContent className="p-4">
            <h3 className="font-medium text-sm text-stone-dark mb-3">Prayers Over Time</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.prayers_by_month} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" />
                <XAxis dataKey="month" tick={TICK_STYLE} tickFormatter={(v) => v.split(" ")[0]} />
                <YAxis tick={TICK_STYLE} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="created" name="Created" fill="#4A6741" radius={BAR_RADIUS_TOP} />
                <Bar dataKey="answered" name="Answered" fill="#D4A373" radius={BAR_RADIUS_TOP} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-stone">
          <CardContent className="p-4">
            <h3 className="font-medium text-sm text-stone-dark mb-3">Prayer Status</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  <Cell fill="#4A6741" />
                  <Cell fill="#D4A373" />
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Requesters */}
      {analytics.top_requesters && analytics.top_requesters.length > 0 && (
        <Card className="border-stone">
          <CardContent className="p-4">
            <h3 className="font-medium text-sm text-stone-dark mb-3">Most Requested By</h3>
            <div className="flex flex-wrap gap-2">
              {analytics.top_requesters.slice(0, 8).map((r) => (
                <span key={r.name} className="inline-flex items-center gap-1.5 bg-stone-light border border-stone rounded-full px-3 py-1 text-xs text-stone-dark">
                  {r.name} <span className="text-stone-dark/40">({r.count})</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tag Breakdown */}
      {analytics.prayers_by_tag && analytics.prayers_by_tag.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid={`${testIdPrefix}-tag-breakdown`}>
          <Card className="border-stone">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-sage" />
                <h3 className="font-medium text-sm text-stone-dark">Prayers by Category</h3>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.prayers_by_tag.slice(0, 8)} margin={CHART_MARGIN} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" horizontal={false} />
                  <XAxis type="number" tick={TICK_STYLE} allowDecimals={false} />
                  <YAxis type="category" dataKey="tag" tick={TICK_STYLE} width={80} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="total" name="Total" fill="#4A6741" radius={BAR_RADIUS_RIGHT} />
                  <Bar dataKey="answered" name="Answered" fill="#D4A373" radius={BAR_RADIUS_RIGHT} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-stone">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-sage" />
                <h3 className="font-medium text-sm text-stone-dark">Category Distribution</h3>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={analytics.prayers_by_tag.slice(0, 8)}
                    cx="50%"
                    cy="50%"
                    outerRadius={75}
                    dataKey="total"
                    nameKey="tag"
                    label={({ tag, percent }) => `${tag} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {analytics.prayers_by_tag.slice(0, 8).map((entry, i) => (
                      <Cell key={entry.tag} fill={TAG_COLORS[i % TAG_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
